/**
 * The catalogue, in the browser (PLAN §16).
 *
 * **This is the rule that inverts.** The old panel rendered every sentence on
 * the server and the browser only ever substituted numbers into them — which is
 * why `web/campaign.ts` contains no Russian and no English, and why tests
 * enforced that. A static frontend cannot keep it: there is no server to render
 * a sentence.
 *
 * What survives is the discipline underneath, and it is the part that mattered.
 * **`domain/` returns keys, never sentences. `smspro` returns `messageKey`,
 * never an English string. Postgres stores `error_key`, never wording.** So
 * there is still exactly one catalogue and exactly one place that turns a key
 * into a sentence. Only that place moved, from the server to here.
 *
 * `readCatalogue` is shared with the server, so a catalogue this would render
 * blanks from is one the server would have refused to boot on.
 */

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  interpolate,
  isLocale,
  readCatalogue,
  type Catalogue,
  type Locale,
  type Translate,
} from '../../messages.ts';

export { LOCALES, type Locale, type MessageKey, type Translate } from '../../messages.ts';

let catalogue: Catalogue | undefined;
let current: Locale = DEFAULT_LOCALE;

/**
 * The language this browser has chosen, or Russian.
 *
 * A cookie rather than `localStorage`, and the same cookie the server used, so
 * a panel that has been half migrated does not flip language when a link
 * crosses between them.
 */
export function chosenLocale(): Locale {
  const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`));
  const value = match?.[1] === undefined ? undefined : decodeURIComponent(match[1]);

  return isLocale(value) ? value : DEFAULT_LOCALE;
}

export function chooseLocale(locale: Locale): void {
  // A year: the choice is a preference, not a session, and outliving one is the
  // point.
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${365 * 24 * 60 * 60}; samesite=lax`;
}

/**
 * Fetches and validates one catalogue. Called once, before anything renders —
 * a screen that appears and then fills in its own words a moment later is worse
 * than a screen that appears late.
 */
export async function loadCatalogue(locale: Locale = chosenLocale()): Promise<Translate> {
  const response = await fetch(`/locales/${locale}.json`, { cache: 'no-cache' });
  if (!response.ok) throw new Error(`Could not load the ${locale} translations: HTTP ${response.status}`);

  catalogue = readCatalogue(await response.json(), `/locales/${locale}.json`);
  current = locale;
  document.documentElement.lang = locale;

  return translate;
}

export function locale(): Locale {
  return current;
}

/**
 * A key that is not in the catalogue is a bug, and it shows as the key rather
 * than as a blank — a blank is invisible in a screenshot and `status.retryFailed`
 * is not.
 */
export const translate: Translate = (key, params) => {
  const message = catalogue?.[key];
  return message === undefined ? key : interpolate(message, params);
};

/**
 * Wording that arrived as a key from somewhere that is not the typed list —
 * `send.7` from the Edge Function, `list-undecided` from `checkCampaign`.
 *
 * Separate from `translate` on purpose: those keys are strings by the time they
 * reach here, and pretending otherwise by casting would remove the one check
 * that `translate` still gives.
 */
export function translateKey(key: string, params?: Readonly<Record<string, string | number>>): string {
  const message = (catalogue as Readonly<Record<string, string>> | undefined)?.[key];
  return message === undefined ? key : interpolate(message, params);
}
